import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import {
  loadInbox,
  loadThread,
  sendMessage,
  toggleReaction,
  markChannelRead,
  createChannel,
  updateChannel,
  addParticipants,
  removeParticipant,
  groupReactions,
  canSendMessages,
  channelLabel,
  summarizeReadState,
  QUICK_REACTIONS,
} from '../../lib/messaging';

/**
 * Coach Messages page — split-pane inbox + thread + composer.
 *
 * Same data shape as the mobile app (the helper lib mirrors the
 * mobile lib) so the rules around viewer / admin / replies-locked
 * stay aligned across clients. The composer is only what's
 * authoritatively missing on mobile: a "+ New" button to create
 * channels, the participant-management menu inside a thread, and
 * the lock-replies toggle for groups.
 */
export function Messages() {
  const { profile } = useAuth();
  // Admin users don't have any athlete_profiles.coach_id pointing
  // at them, so the roster query for new-channel + add-participant
  // needs to drop the coach_id filter for admins. Mirror the same
  // check Athletes.js uses.
  const isAdmin = !!(profile?.is_admin || profile?.role === 'admin');
  const [channels, setChannels] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [thread, setThread] = useState(null);
  const [filter, setFilter] = useState('all'); // all | direct | group | announcement | unread
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const refreshInbox = useCallback(async () => {
    if (!profile?.id) return;
    try {
      const data = await loadInbox(profile.id);
      setChannels(data);
    } catch (e) {
      console.warn('[messaging] loadInbox error:', e?.message ?? e);
    }
  }, [profile?.id]);

  const refreshThread = useCallback(async () => {
    if (!profile?.id || !activeId) return;
    try {
      const t = await loadThread(activeId, profile.id);
      setThread(t);
    } catch (e) {
      console.warn('[messaging] loadThread error:', e?.message ?? e);
    }
  }, [profile?.id, activeId]);

  useEffect(() => {
    let active = true;
    (async () => {
      await refreshInbox();
      if (active) setLoading(false);
    })();
    return () => { active = false; };
  }, [refreshInbox]);

  useEffect(() => {
    if (!activeId) { setThread(null); return; }
    refreshThread();
    markChannelRead(activeId, profile.id).then(() => refreshInbox());
  }, [activeId, refreshThread, refreshInbox, profile?.id]);

  // Realtime: subscribe to messages + reactions for the active
  // channel only. Re-running the thread query on any change is
  // simpler than mutating local state from partial payloads.
  useEffect(() => {
    if (!activeId) return;
    const ch = supabase
      .channel(`web-thread:${activeId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'messages', filter: `channel_id=eq.${activeId}` },
        () => { refreshThread(); refreshInbox(); }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'message_reactions' },
        () => refreshThread()
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [activeId, refreshThread, refreshInbox]);

  const visibleChannels = useMemo(() => {
    let list = channels;
    if (filter === 'unread') {
      list = list.filter(ch => ch.last_message_at &&
        (!ch.last_read_at || new Date(ch.last_read_at) < new Date(ch.last_message_at)));
    } else if (filter !== 'all') {
      list = list.filter(ch => ch.type === filter);
    }
    return list;
  }, [channels, filter]);

  return (
    <Layout>
      <div style={{ marginBottom: 12 }}>
        <p className="page-eyebrow">Messaging</p>
        <h1 className="page-title">Messages</h1>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '320px 1fr',
          gap: 16,
          height: 'calc(100vh - 220px)',
          minHeight: 480,
        }}
      >
        <InboxPane
          channels={visibleChannels}
          loading={loading}
          activeId={activeId}
          onPick={setActiveId}
          filter={filter}
          onFilter={setFilter}
          currentUserId={profile?.id}
          onNew={() => setShowNew(true)}
        />
        <ThreadPane
          thread={thread}
          currentUserId={profile?.id}
          onSend={async content => {
            await sendMessage({ channelId: activeId, senderId: profile.id, content });
            // Realtime will reload, but stamp read so our own
            // message doesn't immediately mark the channel unread.
            await markChannelRead(activeId, profile.id);
          }}
          onToggleReaction={async (messageId, emoji) => {
            await toggleReaction({ messageId, userId: profile.id, emoji });
          }}
          onOpenSettings={() => setShowSettings(true)}
        />
      </div>

      {showNew && (
        <NewChannelModal
          onClose={() => setShowNew(false)}
          createdBy={profile.id}
          isAdmin={isAdmin}
          onCreated={ch => {
            setShowNew(false);
            refreshInbox();
            setActiveId(ch.id);
          }}
        />
      )}

      {showSettings && thread?.channel && (
        <ChannelSettingsModal
          thread={thread}
          currentUserId={profile.id}
          isAdmin={isAdmin}
          onClose={() => setShowSettings(false)}
          onChanged={async () => { await refreshThread(); await refreshInbox(); }}
        />
      )}
    </Layout>
  );
}

// ── Inbox pane ─────────────────────────────────────────────────────

function InboxPane({ channels, loading, activeId, onPick, filter, onFilter, currentUserId, onNew }) {
  const FILTERS = [
    { key: 'all',          label: 'All' },
    { key: 'direct',       label: 'Direct' },
    { key: 'group',        label: 'Groups' },
    { key: 'announcement', label: 'Announcements' },
    { key: 'unread',       label: 'Unread' },
  ];
  return (
    <div style={{
      background: '#fff',
      border: '1px solid var(--gray-200)',
      borderRadius: 10,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--gray-100)', display: 'flex', gap: 8, alignItems: 'center' }}>
        <div style={{ flex: 1, fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>
          Conversations
        </div>
        <button className="btn-xs btn-xs-primary" onClick={onNew}>+ New</button>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: 8, borderBottom: '1px solid var(--gray-100)' }}>
        {FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => onFilter(f.key)}
            style={{
              padding: '4px 10px',
              borderRadius: 999,
              border: '1px solid',
              borderColor: filter === f.key ? 'var(--navy)' : 'var(--gray-200)',
              background: filter === f.key ? 'var(--navy)' : '#fff',
              color: filter === f.key ? '#fff' : 'var(--gray-700)',
              fontSize: 11,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {f.label}
          </button>
        ))}
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <div style={{ padding: 16, color: 'var(--gray-500)', fontSize: 13 }}>Loading…</div>
        ) : channels.length === 0 ? (
          <div style={{ padding: 24, color: 'var(--gray-500)', fontSize: 13, textAlign: 'center' }}>
            No conversations yet.
          </div>
        ) : (
          channels.map(ch => (
            <ChannelRow
              key={ch.id}
              channel={ch}
              active={ch.id === activeId}
              currentUserId={currentUserId}
              onClick={() => onPick(ch.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function ChannelRow({ channel, active, currentUserId, onClick }) {
  const label = channelLabel(channel, currentUserId);
  const isUnread = channel.last_message_at &&
    (!channel.last_read_at || new Date(channel.last_read_at) < new Date(channel.last_message_at));
  const ts = channel.last_message_at ? new Date(channel.last_message_at).toLocaleString('en-US', { month: 'short', day: 'numeric' }) : '';
  const preview = channel.last_message?.content ?? (channel.type === 'announcement' ? 'No announcements yet' : 'Open to start the conversation');
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%',
        textAlign: 'left',
        display: 'flex',
        gap: 10,
        padding: '10px 14px',
        borderBottom: '1px solid var(--gray-100)',
        background: active ? 'rgba(0,24,44,0.05)' : '#fff',
        borderLeft: active ? '3px solid var(--navy)' : '3px solid transparent',
        cursor: 'pointer',
      }}
    >
      <ChannelAvatar channel={channel} currentUserId={currentUserId} label={label} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
          <div style={{ fontSize: 13, fontWeight: isUnread ? 800 : 700, color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {label}
          </div>
          <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{ts}</div>
        </div>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <div style={{
            flex: 1, fontSize: 12,
            color: isUnread ? 'var(--gray-700)' : 'var(--gray-500)',
            fontWeight: isUnread ? 600 : 400,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {preview}
          </div>
          {isUnread ? <div style={{ width: 7, height: 7, borderRadius: 4, background: 'var(--navy)' }} /> : null}
          {channel.flagged ? <span title="Flagged" style={{ fontSize: 10 }}>🚩</span> : null}
        </div>
      </div>
    </button>
  );
}

// ── Thread pane ────────────────────────────────────────────────────

/**
 * Avatar shown left of each inbox row. For direct channels we
 * render the other participant's photo when available (same image
 * the coach detail page already displays); falls back to initials
 * for group / announcement / no-photo cases. Image is clipped via
 * the parent div's overflow:hidden + borderRadius.
 */
function ChannelAvatar({ channel, currentUserId, label }) {
  const isDirect = channel.type === 'direct';
  const isAnnouncement = channel.type === 'announcement';
  const isGroup = channel.type === 'group';
  const other = isDirect
    ? (channel.other_participants ?? channel.participants ?? []).find(p => p.user_id !== currentUserId)
    : null;
  if (other?.avatar_url) {
    return (
      <div
        style={{
          width: 32, height: 32, borderRadius: 16, flexShrink: 0,
          overflow: 'hidden', background: 'var(--gray-100)',
        }}
      >
        <img
          src={other.avatar_url}
          alt=""
          style={{ width: 32, height: 32, objectFit: 'cover', display: 'block' }}
        />
      </div>
    );
  }
  return (
    <div style={{
      width: 32, height: 32, borderRadius: 16, flexShrink: 0,
      background: 'var(--gray-100)', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      fontSize: 14, fontWeight: 700, color: 'var(--navy)',
    }}>
      {isAnnouncement ? '📣' : isGroup ? '👥' : (label[0]?.toUpperCase() ?? '·')}
    </div>
  );
}

function ThreadPane({ thread, currentUserId, onSend, onToggleReaction, onOpenSettings }) {
  // Hooks must run on every render — keep them before the
  // early return for an empty thread.
  const reactionsByMessage = useMemo(() => {
    const m = {};
    (thread?.reactions ?? []).forEach(r => {
      if (!m[r.message_id]) m[r.message_id] = [];
      m[r.message_id].push(r);
    });
    return m;
  }, [thread?.reactions]);

  if (!thread) {
    return (
      <div style={{
        background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 10,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--gray-500)', fontSize: 13,
      }}>
        Pick a conversation to view it
      </div>
    );
  }

  const { channel, myRole, messages, profileById } = thread;
  const composerDisabled = !canSendMessages({ channel, myRole });
  const lockedReason =
    channel.type === 'announcement' && myRole === 'viewer'
      ? 'Announcement — react below'
      : channel.replies_disabled
      ? 'Replies turned off — reactions still on'
      : 'Read-only conversation';

  const title = channelLabel({ ...channel, participants: thread.participants }, currentUserId);
  const subtitle = channel.type === 'direct'
    ? `${thread.participants.length} participants`
    : `${channel.type[0].toUpperCase() + channel.type.slice(1)} · ${thread.participants.length} participants`;

  return (
    <div style={{
      background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 10,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      <div style={{
        padding: '12px 16px', borderBottom: '1px solid var(--gray-100)',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--navy)' }}>{title}</div>
          <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{subtitle}</div>
        </div>
        {(myRole === 'admin' || channel.type !== 'direct') && (
          <button className="btn-xs btn-xs-ghost" onClick={onOpenSettings}>Settings</button>
        )}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {messages.length === 0 ? (
          <div style={{ color: 'var(--gray-500)', fontSize: 13, textAlign: 'center', padding: 32 }}>
            {channel.type === 'announcement' ? 'No announcements yet.' : 'Send the first message.'}
          </div>
        ) : (() => {
          // Index of the most recent of my messages — read state
          // surfaces only on this row to match iMessage / mobile.
          let lastMineIndex = -1;
          for (let i = messages.length - 1; i >= 0; i--) {
            if (messages[i].sender_id === currentUserId) { lastMineIndex = i; break; }
          }
          return messages.map((m, idx) => {
            const prev = messages[idx - 1];
            const showSender = channel.type !== 'direct' && (!prev || prev.sender_id !== m.sender_id);
            const groups = groupReactions(reactionsByMessage[m.id] ?? [], currentUserId);
            const readState = idx === lastMineIndex
              ? summarizeReadState({ message: m, channel, participants: thread.participants, currentUserId })
              : null;
            return (
              <MessageRow
                key={m.id}
                message={m}
                isMine={m.sender_id === currentUserId}
                senderName={profileById[m.sender_id]?.full_name ?? ''}
                showSender={showSender}
                reactions={groups}
                readState={readState}
                onToggleReaction={emoji => onToggleReaction(m.id, emoji)}
              />
            );
          });
        })()}
      </div>

      {composerDisabled ? (
        <div style={{
          padding: '12px 16px', borderTop: '1px solid var(--gray-100)',
          background: 'var(--gray-50)', textAlign: 'center',
          color: 'var(--gray-500)', fontSize: 12, fontWeight: 600,
        }}>
          {lockedReason}
        </div>
      ) : (
        <Composer onSend={onSend} />
      )}
    </div>
  );
}

function MessageRow({ message, isMine, senderName, showSender, reactions, readState, onToggleReaction }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const align = isMine ? 'flex-end' : 'flex-start';
  const bubbleBg = isMine ? 'var(--navy)' : 'var(--gray-100)';
  const fg = isMine ? '#fff' : 'var(--gray-800)';
  const ts = new Date(message.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return (
    <div
      style={{ alignSelf: align, maxWidth: '75%', display: 'flex', flexDirection: 'column', alignItems: align, gap: 2 }}
      onMouseLeave={() => setPickerOpen(false)}
    >
      {showSender && !isMine && senderName ? (
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', marginLeft: 4 }}>{senderName}</div>
      ) : null}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 4, flexDirection: isMine ? 'row-reverse' : 'row' }}>
        <div
          style={{
            background: bubbleBg, color: fg,
            padding: '8px 12px', borderRadius: 12,
            borderBottomRightRadius: isMine ? 3 : 12,
            borderBottomLeftRadius: isMine ? 12 : 3,
            fontSize: 14, lineHeight: 1.4,
            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          }}
        >
          <div>{message.content}</div>
          <div style={{ fontSize: 10, color: isMine ? 'rgba(255,255,255,0.65)' : 'var(--gray-500)', marginTop: 3 }}>
            {ts}{message.edited_at ? ' · edited' : ''}
          </div>
        </div>
        <button
          onClick={() => setPickerOpen(o => !o)}
          title="React"
          style={{
            background: '#fff', border: '1px solid var(--gray-200)',
            width: 24, height: 24, borderRadius: 12,
            cursor: 'pointer', fontSize: 12, padding: 0,
            opacity: pickerOpen ? 1 : 0.7,
          }}
        >☺</button>
        {pickerOpen && (
          <div style={{
            position: 'absolute', top: -36, [isMine ? 'right' : 'left']: 24,
            background: '#fff', border: '1px solid var(--gray-200)',
            borderRadius: 999, padding: 4,
            display: 'flex', gap: 4, boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
            zIndex: 5,
          }}>
            {QUICK_REACTIONS.map(e => (
              <button
                key={e}
                onClick={() => { onToggleReaction(e); setPickerOpen(false); }}
                style={{ background: 'transparent', border: 'none', fontSize: 18, cursor: 'pointer', padding: '2px 6px', borderRadius: 999 }}
              >
                {e}
              </button>
            ))}
          </div>
        )}
      </div>
      {reactions.length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 2 }}>
          {reactions.map(g => (
            <button
              key={g.emoji}
              onClick={() => onToggleReaction(g.emoji)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                padding: '2px 7px', borderRadius: 999,
                border: '1px solid',
                borderColor: g.mine ? 'var(--navy)' : 'var(--gray-200)',
                background: g.mine ? 'rgba(0,24,44,0.08)' : '#fff',
                color: g.mine ? 'var(--navy)' : 'var(--gray-700)',
                fontSize: 11, fontWeight: 700, cursor: 'pointer',
              }}
            >
              <span style={{ fontSize: 12 }}>{g.emoji}</span>
              <span>{g.count}</span>
            </button>
          ))}
        </div>
      )}
      {readState ? (
        <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 2, fontWeight: 600 }}>
          {readState.label}
        </div>
      ) : null}
    </div>
  );
}

function Composer({ onSend }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const taRef = useRef(null);
  const trimmed = text.trim();
  const canSend = !sending && trimmed.length > 0;
  async function handleSend() {
    if (!canSend) return;
    const value = trimmed;
    setText('');
    setSending(true);
    try { await onSend(value); }
    catch (e) { setText(value); }
    finally { setSending(false); }
  }
  return (
    <div style={{ borderTop: '1px solid var(--gray-100)', padding: 12, display: 'flex', gap: 8, alignItems: 'flex-end' }}>
      <textarea
        ref={taRef}
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder="Message"
        onKeyDown={e => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
          }
        }}
        rows={1}
        style={{
          flex: 1, resize: 'none', minHeight: 36, maxHeight: 120,
          padding: '8px 12px', borderRadius: 8,
          border: '1px solid var(--gray-200)', outline: 'none',
          fontFamily: 'inherit', fontSize: 14,
        }}
      />
      <button
        onClick={handleSend}
        disabled={!canSend}
        className="btn btn-primary"
        style={{ width: 'auto', padding: '8px 16px', fontSize: 13, opacity: canSend ? 1 : 0.5 }}
      >
        {sending ? 'Sending…' : 'Send'}
      </button>
    </div>
  );
}

// ── Roster helper ──────────────────────────────────────────────────

/**
 * Loads the athlete roster used by the new-channel modal and the
 * add-participants form. Two-step pattern (matches Athletes.js):
 *
 *   1. athlete_profiles, scoped to coach_id when not admin so
 *      coaches see only their own athletes; admins see everyone.
 *   2. profiles for the resulting ids — gives us full_name + email
 *      without depending on the relational !inner shorthand,
 *      which silently drops rows when the FK relationship name
 *      changes.
 */
async function loadRoster({ currentUserId, isAdmin }) {
  let q = supabase.from('athlete_profiles').select('id, coach_id');
  if (!isAdmin) q = q.eq('coach_id', currentUserId);
  const { data: aps, error } = await q;
  if (error) throw error;
  if (!aps || aps.length === 0) return [];

  const ids = aps.map(a => a.id);
  const { data: profiles } = await supabase
    .from('profiles')
    .select('id, full_name, email')
    .in('id', ids)
    .order('full_name');

  return (profiles ?? []).map(p => ({
    id: p.id,
    full_name: p.full_name ?? '',
    email: p.email ?? '',
  }));
}

// ── New channel modal ──────────────────────────────────────────────

function NewChannelModal({ onClose, createdBy, isAdmin, onCreated }) {
  const [type, setType] = useState('direct');
  const [name, setName] = useState('');
  const [people, setPeople] = useState([]); // [{ id, full_name, email, selected }]
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const roster = await loadRoster({ currentUserId: createdBy, isAdmin });
        if (active) setPeople(roster.map(p => ({ ...p, selected: false })));
      } catch (e) {
        console.warn('[messaging] loadRoster error:', e?.message ?? e);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [createdBy, isAdmin]);

  function togglePerson(id) {
    setPeople(prev => prev.map(p => {
      if (p.id !== id) return p;
      // Direct messages allow only one selection.
      if (type === 'direct' && !p.selected) {
        return { ...p, selected: true };
      }
      return { ...p, selected: !p.selected };
    }).map(p => {
      // Enforce single-select for direct: clear others.
      if (type === 'direct' && p.id !== id) return { ...p, selected: false };
      return p;
    }));
  }

  async function submit() {
    setErr('');
    const selected = people.filter(p => p.selected);
    if (selected.length === 0) {
      setErr('Pick at least one participant.');
      return;
    }
    if (type !== 'direct' && !name.trim()) {
      setErr('Give the conversation a name.');
      return;
    }
    setSubmitting(true);
    try {
      const ch = await createChannel({
        type,
        name: type === 'direct' ? null : name.trim(),
        createdBy,
        participants: selected.map(p => ({ user_id: p.id })),
      });
      onCreated(ch);
    } catch (e) {
      setErr(e?.message ?? String(e));
      setSubmitting(false);
    }
  }

  return (
    <ModalShell title="New conversation" onClose={onClose}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {[
          { key: 'direct',       label: 'Direct' },
          { key: 'group',        label: 'Group' },
          { key: 'announcement', label: 'Announcement' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setType(t.key)}
            style={{
              flex: 1, padding: '10px 12px', borderRadius: 8,
              border: '1.5px solid',
              borderColor: type === t.key ? 'var(--navy)' : 'var(--gray-200)',
              background: type === t.key ? 'var(--navy)' : '#fff',
              color: type === t.key ? '#fff' : 'var(--gray-700)',
              fontSize: 13, fontWeight: 700, cursor: 'pointer',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {type !== 'direct' && (
        <div className="form-group" style={{ marginBottom: 14 }}>
          <label className="form-label">Name</label>
          <input
            type="text"
            className="form-input"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={type === 'announcement' ? 'e.g. Team news' : 'e.g. Sprint group'}
          />
        </div>
      )}

      <div className="form-group" style={{ marginBottom: 14 }}>
        <label className="form-label">
          {type === 'direct' ? 'Who do you want to message?' : type === 'announcement' ? 'Who receives this announcement?' : 'Members'}
        </label>
        <div style={{
          maxHeight: 240, overflowY: 'auto',
          border: '1px solid var(--gray-200)', borderRadius: 8,
        }}>
          {loading ? (
            <div style={{ padding: 12, color: 'var(--gray-500)', fontSize: 13 }}>Loading roster…</div>
          ) : people.length === 0 ? (
            <div style={{ padding: 12, color: 'var(--gray-500)', fontSize: 13 }}>No athletes on your roster yet.</div>
          ) : people.map(p => (
            <button
              key={p.id}
              type="button"
              onClick={() => togglePerson(p.id)}
              style={{
                width: '100%', textAlign: 'left',
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 12px',
                borderBottom: '1px solid var(--gray-100)',
                background: p.selected ? 'rgba(0,24,44,0.05)' : '#fff',
                cursor: 'pointer',
              }}
            >
              <div style={{
                width: 18, height: 18, borderRadius: 4,
                border: '1.5px solid',
                borderColor: p.selected ? 'var(--navy)' : 'var(--gray-300)',
                background: p.selected ? 'var(--navy)' : '#fff',
                color: '#fff', fontSize: 12, lineHeight: '14px', textAlign: 'center', fontWeight: 800,
              }}>
                {p.selected ? '✓' : ''}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)' }}>{p.full_name || p.email}</div>
                {p.full_name && p.email ? (
                  <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{p.email}</div>
                ) : null}
              </div>
            </button>
          ))}
        </div>
      </div>

      {err ? <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 10 }}>{err}</div> : null}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button className="btn-xs btn-xs-ghost" onClick={onClose} disabled={submitting}>Cancel</button>
        <button className="btn btn-primary" onClick={submit} disabled={submitting} style={{ width: 'auto', padding: '8px 16px' }}>
          {submitting ? 'Creating…' : 'Create'}
        </button>
      </div>
    </ModalShell>
  );
}

// ── Channel settings modal ─────────────────────────────────────────

function ChannelSettingsModal({ thread, currentUserId, isAdmin, onClose, onChanged }) {
  const { channel, participants } = thread;
  const myPart = participants.find(p => p.user_id === currentUserId);
  // Channel-admin (the participant role) vs. app admin (profile.is_admin
  // passed in as `isAdmin`) are different things — keep the names
  // distinct so the rest of the file doesn't conflate them.
  // RLS lets either one manage the channel; mirror that here so app
  // admins doing safety follow-up can rename / lock / add as well.
  const isChannelAdmin = myPart?.role === 'admin';
  const canManage = isChannelAdmin || isAdmin;
  const [name, setName] = useState(channel.name ?? '');
  const [repliesDisabled, setRepliesDisabled] = useState(channel.replies_disabled);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function saveBasics() {
    setBusy(true); setErr('');
    try {
      await updateChannel(channel.id, {
        name: name.trim() || null,
        replies_disabled: repliesDisabled,
      });
      await onChanged();
    } catch (e) {
      setErr(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove(userId) {
    if (!window.confirm('Remove this person from the conversation?')) return;
    try {
      await removeParticipant(channel.id, userId);
      await onChanged();
    } catch (e) {
      setErr(e?.message ?? String(e));
    }
  }

  return (
    <ModalShell title="Channel settings" onClose={onClose} wide>
      {channel.type !== 'direct' && (
        <>
          <div className="form-group" style={{ marginBottom: 14 }}>
            <label className="form-label">Name</label>
            <input
              type="text"
              className="form-input"
              value={name}
              onChange={e => setName(e.target.value)}
              disabled={!canManage}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
            <input
              type="checkbox"
              id="lock-replies"
              checked={repliesDisabled}
              onChange={e => setRepliesDisabled(e.target.checked)}
              disabled={!canManage}
            />
            <label htmlFor="lock-replies" style={{ fontSize: 13, color: 'var(--gray-700)' }}>
              Lock replies (members can still react)
            </label>
          </div>

          {canManage && (
            <div style={{ marginBottom: 18 }}>
              <button className="btn btn-primary" onClick={saveBasics} disabled={busy} style={{ width: 'auto', padding: '8px 16px' }}>
                {busy ? 'Saving…' : 'Save'}
              </button>
            </div>
          )}
        </>
      )}

      <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.4, color: 'var(--gray-500)', textTransform: 'uppercase' }}>
          Participants
        </div>
        {canManage && channel.type !== 'direct' && (
          <button className="btn-xs btn-xs-ghost" onClick={() => setAdding(a => !a)}>
            {adding ? 'Cancel' : '+ Add'}
          </button>
        )}
      </div>

      <div style={{ border: '1px solid var(--gray-200)', borderRadius: 8, overflow: 'hidden' }}>
        {participants.map(p => (
          <div key={p.user_id} style={{
            padding: '8px 12px', display: 'flex', alignItems: 'center',
            gap: 10, borderBottom: '1px solid var(--gray-100)', background: '#fff',
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)' }}>{p.full_name || p.email}</div>
              <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{p.role}</div>
            </div>
            {canManage && p.user_id !== currentUserId && channel.type !== 'direct' && (
              <button
                onClick={() => handleRemove(p.user_id)}
                style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
              >
                Remove
              </button>
            )}
          </div>
        ))}
      </div>

      {adding && (
        <AddParticipantsForm
          channel={channel}
          existingIds={new Set(participants.map(p => p.user_id))}
          createdBy={currentUserId}
          isAdmin={isAdmin}
          onAdded={async () => { setAdding(false); await onChanged(); }}
        />
      )}

      {err ? <div style={{ color: '#dc2626', fontSize: 12, marginTop: 10 }}>{err}</div> : null}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
        <button className="btn-xs btn-xs-ghost" onClick={onClose}>Close</button>
      </div>
    </ModalShell>
  );
}

function AddParticipantsForm({ channel, existingIds, createdBy, isAdmin, onAdded }) {
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const roster = await loadRoster({ currentUserId: createdBy, isAdmin });
        if (active) {
          setPeople(
            roster
              .filter(p => !existingIds.has(p.id))
              .map(p => ({ ...p, selected: false }))
          );
        }
      } catch (e) {
        console.warn('[messaging] loadRoster (add) error:', e?.message ?? e);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [createdBy, isAdmin, existingIds]);

  async function submit() {
    const selected = people.filter(p => p.selected);
    if (!selected.length) return;
    setSubmitting(true);
    try {
      await addParticipants(channel.id, selected.map(p => ({
        user_id: p.id,
        role: channel.type === 'announcement' ? 'viewer' : 'member',
      })));
      onAdded();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ marginTop: 12, padding: 12, border: '1px dashed var(--gray-300)', borderRadius: 8 }}>
      {loading ? (
        <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>Loading…</div>
      ) : people.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>Nobody else to add.</div>
      ) : (
        <>
          <div style={{ maxHeight: 180, overflowY: 'auto', marginBottom: 10 }}>
            {people.map(p => (
              <button
                key={p.id}
                type="button"
                onClick={() => setPeople(prev => prev.map(q => q.id === p.id ? { ...q, selected: !q.selected } : q))}
                style={{
                  width: '100%', textAlign: 'left',
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 8px', borderRadius: 6,
                  background: p.selected ? 'rgba(0,24,44,0.06)' : '#fff',
                  border: 'none', cursor: 'pointer',
                  fontSize: 12, color: 'var(--gray-700)',
                }}
              >
                <span>{p.selected ? '☑' : '☐'}</span>
                <span style={{ fontWeight: 600 }}>{p.full_name || p.email}</span>
              </button>
            ))}
          </div>
          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={submitting}
            style={{ width: 'auto', padding: '6px 14px', fontSize: 12 }}
          >
            {submitting ? 'Adding…' : 'Add selected'}
          </button>
        </>
      )}
    </div>
  );
}

// ── Modal shell ────────────────────────────────────────────────────

function ModalShell({ title, onClose, children, wide }) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,24,44,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff', borderRadius: 10, padding: 20,
          width: '100%', maxWidth: wide ? 640 : 480, maxHeight: '90vh', overflowY: 'auto',
          boxShadow: '0 16px 48px rgba(0,0,0,0.18)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--navy)', margin: 0 }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--gray-400)', cursor: 'pointer', padding: 0 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}
